import { Middleware } from '@reduxjs/toolkit'
import type { RelayedAction } from './protocol'

export type Relay = {
	/** For the host window's store, after the app's own middlewares. */
	middleware: Middleware
	/** Hands applied actions to `host` until the returned detach is called. */
	attach: (host: { relay: (action: RelayedAction) => void }) => () => void
}

const isPlainAction = (action: unknown): action is RelayedAction =>
	typeof action === 'object' &&
	action !== null &&
	typeof (action as { type?: unknown }).type === 'string'

/**
 * Hands every plain action the host's store has applied to the running host
 * end, which forwards the ones its sync policy relays. After `next`, so the
 * replica applies it in the same order as this window did. One per store:
 * the store is built before any link runs, the link attaches when it starts.
 */
export const createRelay = (): Relay => {
	let host: { relay: (action: RelayedAction) => void } | null = null
	return {
		middleware: () => next => action => {
			const result = next(action)
			if (host && isPlainAction(action)) {
				host.relay(action)
			}
			return result
		},
		attach: target => {
			host = target
			return () => {
				if (host === target) {
					host = null
				}
			}
		},
	}
}
